import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Button} from '@/components/ui/button/Button'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {CheckIcon, Download01Icon} from '@/components/ui/icon/Icon'
import {
    StyledExportSiarDialogBody,
    StyledExportSiarDialogFooter,
    StyledSiarTypeHint,
    StyledSiarTypeOption,
    StyledSiarTypeOptionCheckmark,
    StyledSiarTypeOptionsContainer,
    StyledSiarTypeSelectLabel
} from '@/features/projects/components/export-siar-dialog/style'
import {ExportSiarTrigger} from '@/features/projects/components/export-siar-trigger/ExportSiarTrigger'
import {ExportSiarType, ProjectIndex} from '@/features/projects/types'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import {ComponentType, MouseEvent, useMemo, useState} from 'react'

export const ExportSiarDialog = ({
    projects,
    customTrigger: CustomTrigger
}: {
    projects: ProjectIndex[]
    customTrigger?: ComponentType<{onClick: (event?: MouseEvent<HTMLElement>) => void; isDisabled: boolean}>
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)

    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: setIsOpen
    })

    const isEnabled = useMemo(() => {
        return projects.every(project => project.status !== 'opening')
    }, [projects])

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    CustomTrigger ? (
                        <CustomTrigger onClick={() => setIsOpen(true)} isDisabled={!isEnabled} />
                    ) : (
                        <Button onClick={() => setIsOpen(true)} disabled={!isEnabled}>
                            <Download01Icon />
                            {t('projects:export_siar')}
                        </Button>
                    )
                }
            />
            <Dialog width="480px">{isOpen && <ExportSiarDialogContent projects={projects} />}</Dialog>
        </DialogProvider>
    )
}

const ExportSiarDialogContent = ({projects}: {projects: ProjectIndex[]}) => {
    const {t} = useTranslation()
    const [selectedType, setSelectedType] = useState<ExportSiarType>('open_project')
    const [addRegistry, setAddRegistry] = useState(false)

    const dialog = useDialogContext()

    const handleExportSuccess = () => {
        void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
        void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
        projects.forEach(project => {
            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
        })
        dialog?.setOpen(false)
    }

    return (
        <>
            <DialogHeader>
                <DialogHeading>{t('projects:export_siar')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            <StyledExportSiarDialogBody>
                <StyledSiarTypeSelectLabel>{t('projects:export_siar_select_type')}</StyledSiarTypeSelectLabel>
                <StyledSiarTypeOptionsContainer>
                    <StyledSiarTypeOption
                        type="button"
                        $isSelected={selectedType === 'open_project'}
                        onClick={() => setSelectedType('open_project')}
                    >
                        <div>
                            <h4>{t('projects:export_siar_opening_title')}</h4>
                            <p>{t('projects:export_siar_opening_description')}</p>
                        </div>
                        <StyledSiarTypeOptionCheckmark $isSelected={selectedType === 'open_project'}>
                            <CheckIcon size={20} />
                        </StyledSiarTypeOptionCheckmark>
                    </StyledSiarTypeOption>
                    <StyledSiarTypeOption
                        type="button"
                        $isSelected={selectedType === 'close_project'}
                        onClick={() => setSelectedType('close_project')}
                    >
                        <div>
                            <h4>{t('projects:export_siar_closing_title')}</h4>
                            <p>{t('projects:export_siar_closing_description')}</p>
                        </div>
                        <StyledSiarTypeOptionCheckmark $isSelected={selectedType === 'close_project'}>
                            <CheckIcon size={20} />
                        </StyledSiarTypeOptionCheckmark>
                    </StyledSiarTypeOption>
                </StyledSiarTypeOptionsContainer>
                <StyledSiarTypeHint>{t('projects:export_siar_any_order')}</StyledSiarTypeHint>
                <Checkbox
                    id="export-siar-anagraphic"
                    label={t('projects:export_siar_anagraphic')}
                    checked={addRegistry}
                    onChange={() => setAddRegistry(!addRegistry)}
                />
            </StyledExportSiarDialogBody>
            <StyledExportSiarDialogFooter>
                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
                <ExportSiarTrigger
                    exportSiarType={selectedType}
                    projectIds={projects.map(project => project.id)}
                    addRegistry={addRegistry}
                    onExportSuccess={handleExportSuccess}
                />
            </StyledExportSiarDialogFooter>
        </>
    )
}
